import * as Sentry from "@sentry/vue";

const RELOAD_MARKER = "atmo:stale-build-reload";
const RELOAD_COOLDOWN_MS = 60 * 1000;
const SENTRY_FLUSH_TIMEOUT_MS = 2000;
const SW_ACTIVATION_TIMEOUT_MS = 2000;

const CASCADE_WINDOW_MS = 30 * 1000;
const MAX_CASCADE_EVENTS = 1;

export const STALE_BUILD_FINGERPRINT = ["atmo", "stale-build"];

const DYNAMIC_IMPORT_FAILURE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to load module script|Expected a JavaScript(?:-or-Wasm)? module script|Couldn't resolve component/i;

const cascade = { eventId: null, at: 0, kept: 0 };

const readReloadMarker = () => {
  try {
    return Number(sessionStorage.getItem(RELOAD_MARKER)) || 0;
  } catch {
    return 0;
  }
};

const writeReloadMarker = (at) => {
  try {
    sessionStorage.setItem(RELOAD_MARKER, String(at));
  } catch {
    // Storage inaccessible (navigation privée)
  }
};

export const isDynamicImportError = (error) =>
  DYNAMIC_IMPORT_FAILURE.test(error?.message ?? "");

// Une seule remontée par onglet : la même panne
// traverse router.onError ET vite:preloadError. Capturer l'erreur d'origine
// la marque __sentry_captured__, ce qui neutralise la capture que @sentry/vue
// fait de son côté sur router.onError.
export const reportStaleBuild = ({ reason, routeName, error }) => {
  if (cascade.eventId) return cascade.eventId;

  cascade.at = Date.now();
  cascade.eventId = Sentry.captureException(error, {
    mechanism: { type: "atmo.stale-build", handled: false },
    captureContext: {
      level: "error",
      fingerprint: STALE_BUILD_FINGERPRINT,
      tags: {
        stale_build: "true",
        stale_build_reason: reason,
        stale_build_route: routeName ?? "unknown",
        cascade_role: "cause",
      },
    },
  });

  Sentry.setTags({ stale_build: "true", cascade_of: cascade.eventId });
  return cascade.eventId;
};

// "group" replie l'erreur sur l'issue de la cause,
// "drop" abandonne les suivantes : une conséquence suffit à documenter la casse.
export const claimCascadeSlot = () => {
  if (!cascade.eventId) return "none";
  if (Date.now() - cascade.at > CASCADE_WINDOW_MS) return "none";

  cascade.kept += 1;
  return cascade.kept <= MAX_CASCADE_EVENTS ? "group" : "drop";
};

// En registerType "prompt" le SW en place répond depuis son précache :
// recharger sans l'activer ramène le même build mort.
export const activateWaitingWorker = async () => {
  if (!navigator.serviceWorker?.controller) return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.waiting) return;

  registration.waiting.postMessage({ type: "SKIP_WAITING" });

  await new Promise((resolve) => {
    const controller = new AbortController();
    const done = () => {
      controller.abort();
      clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = setTimeout(done, SW_ACTIVATION_TIMEOUT_MS);
    navigator.serviceWorker.addEventListener("controllerchange", done, {
      signal: controller.signal,
    });
  });
};

export const reloadAfterStaleBuild = async (href) => {
  const now = Date.now();
  if (now - readReloadMarker() < RELOAD_COOLDOWN_MS) return false;

  writeReloadMarker(now);
  await Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS);
  await activateWaitingWorker();

  globalThis.location.assign(href);
  return true;
};

export const flagStaleBuildReload = () => {
  const at = readReloadMarker();
  if (!at || Date.now() - at > RELOAD_COOLDOWN_MS) return;

  Sentry.setTags({
    stale_build_reload: "true",
    stale_build_reloaded_at: new Date(at).toISOString(),
  });
};
